import { cloneDeep } from "lodash";
import { ReportIncomeFilterOptions } from "./options/ReportIncomeFilterOptions";
import { allClassificationTypesOptions, allDataSourcesOptions, allPeriodicitiesOptions, allReportNamesOptions, allReportTypesOptions, allMutuallyRedeemingOptions, allStatusOptions, allBudgetTypesOptions, allMutuallyRedeemingLevelOptions, allSegmentationOptions, allMeasureUnitsOptions } from "../../common/baseOptions";
import { Indicator } from "../../common/types/Indicator";
import { ReportIncomeFilterSettings } from "./settings/ReportIncomeFilterSettings";
import { Template } from "../../common/types/Template";
import { Option } from "../../common/types/Option";
import { MeasureUnit, Segmentation } from "../../common/types/roots/fields";
import { ReportIncomeFilterSettingsForm } from "./settings-form/ReportIncomeFilterSettingsForm";
import { Draggable } from "../../common/types/Draggable";
import { Classificator } from "../../common/types/Classificator";

export const reportIncomeIndicators: Indicator[] = [
    { key: 'action', active: false },
    { key: 'kat', active: false },
    { key: 'cls', active: false },
    { key: 'pcl', active: false },
    { key: 'spf', active: false },
    { key: 'region_name', active: true },
    { key: 'total_sum', active: true },
    { key: 'period_sum', active: true },
    { key: 'sumrg', active: true },
    { key: 'deviation_period', active: true },
    { key: 'percent_to_period', active: true },
    { key: 'deviation_annual', active: true },
    { key: 'percent_to_year', active: true },
    { key: 'total_sum_past', active: false },
    { key: 'period_sum_past', active: false },
    { key: 'sumrg_past', active: false },
    { key: 'deviation_annual_past', active: false },
    { key: 'deviation_period_past', active: false },
    { key: 'deviation_past', active: false },
    { key: 'growth', active: false },
]

export const allIncomeClassificators: Classificator[] = [
    { key: 'kat', active: true, selected: [1, 2, 3] },
    { key: 'cls', active: true, selected: [] },
    { key: 'pcl', active: true, selected: [] },
    { key: 'spf', active: true, selected: [] }
]


const reportIncomeSegmentationValues = [
    'REGION',
    'KBK'
]

export const reportIncomeSegmentationOptions = allSegmentationOptions.filter(
    v => reportIncomeSegmentationValues.includes(v.value)
)


/** Служебные колонки таблицы (действие, КБК, наименование) — не показатели */
export const reportIncomeServiceIndicatorKeys: string[] = ['action', 'kat', 'cls', 'pcl', 'spf']

/** Процентные показатели: без «Округлять до» на экране показываются с 2 знаками */
export const reportIncomePercentKeys: string[] = ['percent_to_period', 'percent_to_year', 'deviation_past']

/** Активные сегменты разрезности по порядку */
export function getActiveIncomeSegments(segmentations: Draggable<Segmentation>[] | null | undefined): Segmentation[] {
    return segmentations?.filter(s => s.active).map(s => s.value) ?? []
}

export interface IncomeApplicability {
    isTree: boolean
    prevYear: boolean
    regionsTotal: boolean
    decodingActualRegionalBudget: boolean
    nameBeforeKbk: boolean
}

/**
 * Какие переключатели INCOME применимы при данной разрезности (сегменты по порядку) и isTree.
 * Единый источник правды для фильтра (disabled/сброс), фасада и экрана.
 */
export function getIncomeApplicability(segs: readonly string[], isTree: boolean): IncomeApplicability {
    const onlyRegion = segs.length === 1 && segs[0] === 'REGION'
    const onlyKbk = segs.length === 1 && segs[0] === 'KBK'
    const regionKbk = segs.length === 2 && segs[0] === 'REGION' && segs[1] === 'KBK'
    return {
        isTree: onlyKbk,
        prevYear: onlyRegion || onlyKbk || regionKbk,
        regionsTotal: onlyRegion,
        decodingActualRegionalBudget: onlyRegion,
        nameBeforeKbk: (onlyKbk && !!isTree) || regionKbk,
    }
}

/**
 * «Эффективная» форма: копия, где недоступные переключатели выключены.
 * Исходную форму (и шаблон) не меняет — применяется только при построении отчёта/экрана.
 */
export function getEffectiveIncomeForm(form: ReportIncomeFilterSettingsForm): ReportIncomeFilterSettingsForm {
    const segs = getActiveIncomeSegments(form.segmentations)
    const ok = getIncomeApplicability(segs, !!form.isTree)
    return {
        ...form,
        isTree: ok.isTree && !!form.isTree,
        prevYear: ok.prevYear && !!form.prevYear,
        regionsTotal: ok.regionsTotal && !!form.regionsTotal,
        decodingActualRegionalBudget: ok.decodingActualRegionalBudget && !!form.decodingActualRegionalBudget,
        nameBeforeKbk: ok.nameBeforeKbk && !!form.nameBeforeKbk,
    }
}

// Нужна как временная затычка (При созданий нового берутся пустые настройки)
const tempSettings: ReportIncomeFilterSettings = {
    // Base:
    reportName: 'INCOME',
    reportType: 'REGULATED',
    classificationType: 'INCOMES',
    periodicity: 'DAY',
    measureUnit: 'TENGE',
    roundUpTo: null,
    // Custom:
    regions: [],
    dataSource: 'IISK',
    date: '',
    segmentations: [],
    mutuallyRedeeming: 'WITHOUT',
    decodingActualRegionalbudget: false,
    isTree: false,
    indicators: cloneDeep(reportIncomeIndicators),
    incomes: cloneDeep(allIncomeClassificators),
    statuses: ['39'],
    regionsTotal: false,
    prevYear: false,
    budgetTypes: [],
    mutuallyRedeemingLevels: [],
    sortByRegionCode: false,
    nameBeforeKbk: false
}

export const reportIncomeBaseTemplate: Template = {
    id: null,
    name: 'Информация об исполнении доходов бюджета',
    excelName: null,
    description: null,
    category: '100',
    regions: [],
    userId: '',
    filterSettings: tempSettings
}

export const reportIncomeFilterOptions: ReportIncomeFilterOptions = {
    // Base:
    key: 'INCOME',
    reportNames: cloneDeep(allReportNamesOptions),
    reportTypes: cloneDeep(allReportTypesOptions),
    classificationTypes: cloneDeep(allClassificationTypesOptions),
    dataSources: cloneDeep(allDataSourcesOptions.filter(it => it.value !== 'MIXED')),
    periodicities: cloneDeep(allPeriodicitiesOptions),
    incomes: cloneDeep(allIncomeClassificators),
    budgetTypes: cloneDeep(allBudgetTypesOptions).filter(it => it.value !== 1),
    // Custom:
    regions: [], // Загружается с бэка
    indicators: cloneDeep(reportIncomeIndicators),
    segmentations: cloneDeep(reportIncomeSegmentationOptions),
    mutuallyRedeeming: cloneDeep(allMutuallyRedeemingOptions),
    measureUnits: cloneDeep(allMeasureUnitsOptions),
    statuses: cloneDeep(allStatusOptions),
    mutuallyRedeemingLevels: cloneDeep(allMutuallyRedeemingLevelOptions)
}
